import { ReactNode, SyntheticEvent } from "react"
import { SupportedTokenName } from "@mezo/sdk"
import {
  ModalBody,
  ModalFooter,
  ParagraphSmall,
  useStyletron,
} from "@mezo-org/mezo-clay"
import { Form, FormikProps, withFormik } from "formik"

import { useEstimateVeBTCValue } from "#/shared/hooks"
import { StakingLockDurationType } from "#/shared/types"
import { floatToBigInt } from "#/shared/utils"
import { StepperModalButton } from "#/ui/Modal"

import LockDuration from "./LockDuration"
import StakingItemContainer from "./StakingItemContainer"
import StakingRewardsLabel from "./StakingRewardsLabel"

export type StakingFormValue = {
  amount: string
  lockDuration: StakingLockDurationType
}

type StakingFormBaseProps = {
  token: SupportedTokenName
  tokenDecimals: number
  balance: string
  withLockDuration?: boolean
  buttonLabel?: ReactNode
  onButtonClick?: (event: SyntheticEvent<HTMLButtonElement>) => void
}

type StakingFormProps = StakingFormBaseProps & {
  initialValues: StakingFormValue
  onSubmitForm: (values: StakingFormValue) => void
  shouldSkipValidation?: boolean
}

function StakingFormBase({
  handleSubmit,
  // token,
  // balance,
  // tokenDecimals,
  isValid,
  values,
  withLockDuration = false,
  buttonLabel = "Continue",
  onButtonClick,
}: StakingFormBaseProps & FormikProps<StakingFormValue>) {
  const [css, theme] = useStyletron()

  const veBTC = useEstimateVeBTCValue(
    floatToBigInt(values.amount),
    values.lockDuration,
  )

  // const balanceUsd = useBTCToUSDConversion(values.amount)

  return (
    <Form
      onSubmit={handleSubmit}
      className={css({
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      })}
    >
      <ModalBody
        className={css({
          display: "flex",
          flexDirection: "column",
          gap: theme.sizing.scale300,
        })}
      >
        {/* TODO: Uncomment once we get back to the staking feature. We must
        adjust props we pass to the FieldAmountInput component to changes
        from: https://github.com/thesis/mezo-portal/pull/2121 */}
        {/* <FieldAmountInput
          fieldName="amount"
          token={token}
          label="Enter stake amount"
          tokenDecimals={tokenDecimals}
          balanceToken={balance}
          balanceUsd={separateThousandsWithComma(balanceUsd ?? 0, 0)}
          autoFocus
        /> */}
        {withLockDuration && (
          <LockDuration
            fieldName="lockDuration"
            stakeAmount={floatToBigInt(values.amount)}
          />
        )}
      </ModalBody>
      <ModalFooter
        className={css({
          display: "flex",
          flexDirection: "column",
          gap: theme.sizing.scale800,
        })}
      >
        <StakingItemContainer
          alignItems="center"
          backgroundColor={theme.colors.lime300}
          padding={`${theme.sizing.scale500} ${theme.sizing.scale700}`}
        >
          <ParagraphSmall>
            {values.amount ? "You’ll receive" : "Enter amount to see rewards"}
          </ParagraphSmall>
          <StakingRewardsLabel veBTC={veBTC} />
        </StakingItemContainer>
        <StepperModalButton
          disabled={!isValid}
          type="submit"
          {...(typeof onButtonClick === "function" && {
            onClick: onButtonClick,
            type: "button",
          })}
        >
          {buttonLabel}
        </StepperModalButton>
      </ModalFooter>
    </Form>
  )
}

const StakingForm = withFormik<StakingFormProps, StakingFormValue>({
  mapPropsToValues: ({ initialValues }) => initialValues,
  // TODO: Uncomment once we get back to the staking feature. We must adjust
  // validation to changes from: https://github.com/thesis/mezo-portal/pull/2121
  // validate: ({ amount }, { balance, token, shouldSkipValidation }) => {
  //   if (shouldSkipValidation) return {}

  //   const errors: FormikErrors<StakingFormValue> = {}

  //   // errors.amount = formUtils.validateTokenAmount(amount, balance, token)

  //   if (errors.amount) return errors

  //   return {}
  // },
  handleSubmit: (values, { props }) => {
    props.onSubmitForm(values)
  },
  validateOnBlur: false,
})(StakingFormBase)

export default StakingForm
